import { useActionState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError, api } from '../api/client';
import { allowed, session } from '../auth/session';
import { landingPath } from '../layout/navigation';
import { NorvaAppIcon, NorvaWordmark } from '../brand';
import { IconArrowRight, IconInfo, IconLanguages, IconLock, IconMail } from '../icons';
import { LANGUAGES, language, setLanguage, type Language } from '../i18n';
import { useTranslate } from '../i18n/react';
import { Button } from '../ui/Button';
import { Alert, Field, PasswordInput, Select, TextInput } from '../ui/Field';

type FormState = { error: 'required' | 'rejected' | 'unreachable' | null; email: string };

/**
 * The first screen, and the only one that has to introduce the product.
 *
 * <p>Split in two: the brand on the left, the form on the right, the language in the top
 * corner where somebody who cannot read the form can still reach it. Below 1024px the brand
 * panel goes and the logo moves above the form.</p>
 */
export function Login() {
  const navigate = useNavigate();
  const t = useTranslate();

  // React 19's form action gives the pending state and the result without a form library.
  const [state, submit, pending] = useActionState<FormState, FormData>(
    async (_previous, form) => {
      const email = String(form.get('email') ?? '').trim();
      const password = String(form.get('password') ?? '');

      if (!email || !password) return { error: 'required', email };

      try {
        await api.signIn(email, password);

        // Where this person can actually work. The token has just arrived, so the
        // permissions it carries are readable now.
        navigate(landingPath((p) => allowed(session(), p)), { replace: true });
        return { error: null, email };
      } catch (e) {
        // One message for a wrong address and a wrong password, because the server
        // deliberately will not say which: telling the caller would turn this form into a
        // way of finding out who has an account.
        //
        // But a server that cannot be reached at all is a different sentence. Saying
        // "parola hatalı" to somebody whose network is down — or whose API is not
        // running — sends them away to change a password that was never the problem.
        const refused = e instanceof ApiError && (e.status === 401 || e.status === 400);
        return { error: refused ? 'rejected' : 'unreachable', email };
      }
    },
    { error: null, email: '' },
  );

  return (
    <main className="nv-login">
      <section className="nv-login__brand" aria-hidden="true">
        <NorvaWordmark className="nv-login__logo" />

        <svg className="nv-login__mark" viewBox="0 0 600 600">
          <circle cx="300" cy="300" r="250" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="12" />
          <circle cx="300" cy="300" r="178" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="1.5" />
          <polygon
            points="300,96 331,269 504,300 331,331 300,504 269,331 96,300 269,269"
            fill="none"
            stroke="rgba(251,191,36,0.45)"
            strokeWidth="2"
          />
        </svg>

        <div className="nv-login__copy">
          <h1>
            {t('login.brand.headline')}
            <br />
            <em>{t('login.brand.headlineAccent')}</em>
          </h1>
          <p>{t('login.brand.text')}</p>
        </div>

        <div className="nv-login__foot">{t('login.brand.foot', { year: new Date().getFullYear() })}</div>
      </section>

      <section className="nv-login__side">
        <div className="nv-login__lang">
          <IconLanguages size={16} />
          <Select
            aria-label={t('app.language')}
            value={language()}
            onChange={(e) => setLanguage(e.target.value as Language)}
          >
            {LANGUAGES.map((l) => (
              <option key={l.code} value={l.code}>{l.label}</option>
            ))}
          </Select>
        </div>

        <form className="nv-login__form" action={submit}>
          <NorvaAppIcon className="nv-login__icon" />

          <h2>{t('login.title')}</h2>
          <p className="nv-login__lead">{t('login.lead')}</p>

          {state.error ? (
            <Alert tone={state.error === 'unreachable' ? 'warning' : 'danger'}>
              {t(
                state.error === 'required'
                  ? 'login.required'
                  : state.error === 'unreachable'
                    ? 'login.unreachable'
                    : 'login.failed',
              )}
            </Alert>
          ) : null}

          <Field label={t('login.email')}>
            {({ id }) => (
              <TextInput
                id={id}
                name="email"
                type="email"
                icon={IconMail}
                large
                required
                autoComplete="username"
                autoFocus
                placeholder="ad.soyad@sirket.com"
                defaultValue={state.email}
              />
            )}
          </Field>

          <Field label={t('login.password')}>
            {({ id }) => (
              <PasswordInput id={id} name="password" icon={IconLock} large required autoComplete="current-password" />
            )}
          </Field>

          <Button
            type="submit"
            variant="primary"
            size="lg"
            block
            loading={pending}
            loadingText={t('login.working')}
            trailingIcon={IconArrowRight}
          >
            {t('login.submit')}
          </Button>

          {/* There is no password reset yet. Saying so is better than a link that goes nowhere. */}
          <p className="nv-login__help">
            <IconInfo size={16} />
            <span>{t('login.help')}</span>
          </p>
        </form>
      </section>
    </main>
  );
}
